import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAgencyById,
  editAgency,
  getUsers,
  getIndustries,
  getServices
} from '../../services/firestoreService';
import { uploadImage } from '../../utils/uploadImage';
import PageHead from '../../components/common/PageHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faImage } from '@fortawesome/free-solid-svg-icons';

const EditAgency = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    agencyName: '',
    userId: '',
    logo: null,
    description: '',
    services: [],
    industry: '',
    email: '',
    phone: '',
    website: '',
    testimonials: [''],
    pricings: [''],
    caseStudies: [''],
    isApproved: true,
  });

  const [logoName, setLogoName] = useState('');
  const [existingLogoUrl, setExistingLogoUrl] = useState('');
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const fetchAgency = async () => {
      try {
        const data = await getAgencyById(agencyId);
        if (!data) return setError('Agency not found');
        setFormData({
          ...formData,
          agencyName: data.name || '',
          userId: data.userId || '',
          description: data.description || '',
          services: data.services || [],
          industry: data.industry || '',
          email: data.email || '',
          phone: data.phone || '',
          website: data.website || '',
          testimonials: data.testimonials || [''],
          pricings: data.pricings || [''],
          caseStudies: data.caseStudies || [''],
          isApproved: data.isApproved ?? true,
        });
        setExistingLogoUrl(data.logo || '');
        setLogoName(data.logo ? 'Current Logo' : '');
      } catch (err) {
        setError('Failed to load agency data.');
        console.error(err);
      }
    };

    const fetchMeta = async () => {
      setUsers(await getUsers());
      setFilteredUsers(await getUsers());
      setServices(await getServices());
      setIndustries(await getIndustries());
    };

    fetchAgency();
    fetchMeta();
  }, [agencyId]);

  const handleChange = (e) => {
    const { name, value, files, checked } = e.target;
    if (name === 'logo') {
      setFormData({ ...formData, logo: files[0] });
      setLogoName(files[0]?.name || '');
    } else if (name === 'services') {
      const updatedServices = checked
        ? [...formData.services, value]
        : formData.services.filter((s) => s !== value);
      setFormData({ ...formData, services: updatedServices });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleUserSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredUsers(users.filter(user => user.username.toLowerCase().includes(query)));
  };

  const handleUserSelect = (userId) => {
    setFormData({ ...formData, userId });
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logo: null });
    setLogoName('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setError('');
    try {
      let logoUrl = existingLogoUrl;
      if (formData.logo) {
        const path = 'agency_logos/admin_uploaded_logos/';
        logoUrl = await uploadImage(formData.logo, path);
      }

      const agencyData = {
        name: formData.agencyName,
        userId: formData.userId,
        logo: logoUrl,
        description: formData.description,
        services: formData.services,
        industry: formData.industry,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        testimonials: formData.testimonials,
        pricings: formData.pricings,
        caseStudies: formData.caseStudies,
        isApproved: formData.isApproved,
        dateUpdated: new Date(),
      };

      await editAgency(agencyId, agencyData);
      setSuccessMessage('Agency updated successfully!');
      setTimeout(() => navigate('/admin/agencies'), 3000);
    } catch (err) {
      setError('Failed to update agency.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-full mx-auto">
      <PageHead name="Edit Agency" />
      <div className="flex items-center justify-center my-16 mx-2">
        <form className="space-y-8 text-grey-600 w-full md:w-2/3" onSubmit={handleSubmit}>
          {/* Error or Success Messages */}
          {error && <p className="text-red-500">{error}</p>}
          {successMessage && <p className="text-green-500 font-semibold">{successMessage}</p>}

          {/* Agency Name and Logo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              name="agencyName"
              value={formData.agencyName}
              placeholder="Agency Name"
              onChange={handleChange}
              className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs"
            />
            <label className="block p-3 border border-gray-600 border-2 rounded-xs text-gray-500 cursor-pointer">
              {logoName ? (
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faImage} className="text-lg mr-3" />
                  <span className="truncate">{logoName}</span>
                  <button type="button" onClick={handleRemoveLogo} className="ml-2 text-red-500">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} className="text-lg mr-3" />
                  Upload Logo
                </>
              )}
              <input type="file" name="logo" accept="image/*" onChange={handleChange} className="sr-only" />
            </label>
          </div>

          {/* User Assignment */}
          <div className="space-y-4">
            <label className="font-bold">Assign User</label>
            <input
              type="text"
              placeholder="Search User"
              value={searchQuery}
              onChange={handleUserSearch}
              className="w-full p-3 border border-gray-600 border-2 rounded-xs"
            />
            <ul className="border border-gray-600 rounded-md max-h-40 overflow-y-auto">
              {filteredUsers.map(user => (
                <li
                  key={user.id}
                  onClick={() => handleUserSelect(user.id)}
                  className={`p-2 cursor-pointer hover:bg-gray-200 ${formData.userId === user.id ? 'bg-blue-100' : ''}`}
                >
                  {user.username}
                </li>
              ))}
            </ul>
            {formData.userId && (
              <p className="text-sm text-gray-600 mt-2">
                Selected user: {users.find(user => user.id === formData.userId)?.username || 'Unknown'}
              </p>
            )}
          </div>

          {/* Description */}
          <textarea
            name="description"
            placeholder="Tell Us About Your Agency"
            onChange={handleChange}
            value={formData.description}
            className="w-full p-3 border border-gray-600 border-2 rounded-xs h-32"
          ></textarea>

          {/* Services */}
          <div className="space-y-5">
            <label className="font-bold text-lg">Services Offered:</label>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              {services.map(service => (
                <label key={service.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="services"
                    value={service.name}
                    checked={formData.services.includes(service.name)}
                    onChange={handleChange}
                    className="form-checkbox h-4 w-4 text-primary"
                  />
                  <span>{service.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Industry Dropdown */}
          <select
            name="industry"
            onChange={handleChange}
            value={formData.industry}
            className="w-full p-3 border border-gray-600 border-2 rounded-xs"
          >
            <option value="" disabled>Select Industry</option>
            {industries.map(ind => (
              <option key={ind.id} value={ind.name}>
                {ind.name}
              </option>
            ))}
          </select>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input type="text" name="email" value={formData.email} placeholder="Email" onChange={handleChange}
              className="w-full p-3 border border-gray-600 border-2 rounded-xs" />
            <input type="text" name="phone" value={formData.phone} placeholder="Phone" onChange={handleChange}
              className="w-full p-3 border border-gray-600 border-2 rounded-xs" />
          </div>

          <input type="text" name="website" value={formData.website} placeholder="Website" onChange={handleChange}
            className="w-full p-3 border border-gray-600 border-2 rounded-xs" />

          <div className="mb-24 flex justify-center">
            <button type="submit" disabled={isUploading}
              className="bg-primary text-white text-lg px-6 py-2 hover:bg-blue-600 rounded disabled:opacity-50">
              {isUploading ? 'Updating...' : 'Update Agency'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAgency;
